import React, { useState, useEffect } from 'react';
import { InstallationStepsModal } from './index';
import { loadWins } from '@/features/game/persistence';
import { getTodaysRiddle } from '@/features/game/logic';

// Extend Window interface for PWA install prompt
declare global {
  interface Window {
    deferredPrompt?: {
      prompt: () => void;
      userChoice: Promise<{ outcome: string }>;
    };
  }
}

interface StartScreenProps {
  onStart: () => void;
  onHowToPlay: () => void;
}

const StartScreen: React.FC<StartScreenProps> = ({ onStart, onHowToPlay }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [hasPlayedToday, setHasPlayedToday] = useState(false);

  useEffect(() => {
    // Check if device is mobile and if app is running as PWA with error handling
    const checkDeviceAndPWA = () => {
      try {
        const userAgent = navigator.userAgent.toLowerCase();
        const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
        
        // Check if app is running as PWA (standalone mode)
        const isPWAMode = window.matchMedia('(display-mode: standalone)').matches || 
                          ('standalone' in window.navigator && (window.navigator as Navigator & { standalone?: boolean }).standalone === true) ||
                          document.referrer.includes('android-app://');
        
        console.log('📱 Device and PWA check:', { userAgent, isMobileDevice, isPWAMode });
        setIsMobile(isMobileDevice);
        setIsPWA(isPWAMode);
      } catch (error) {
        console.error('❌ Device/PWA detection error:', error);
        // Fallback to false if detection fails
        setIsMobile(false);
        setIsPWA(false);
      }
    };

    // Check if user has already played today's game
    const checkIfPlayedToday = () => {
      try {
        const wins = loadWins();
        const todaysRiddle = getTodaysRiddle();
        const hasPlayed = wins.includes(todaysRiddle.answer);
        setHasPlayedToday(hasPlayed);
        console.log('🎮 Play status check:', { 
          todaysAnswer: todaysRiddle.answer, 
          wins, 
          hasPlayed 
        });
      } catch (error) {
        console.error('❌ Play status check error:', error);
        setHasPlayedToday(false);
      }
    };

    // Initial checks
    checkDeviceAndPWA();
    checkIfPlayedToday();
    
    // Add resize listener with error handling
    try {
      window.addEventListener('resize', checkDeviceAndPWA);
      return () => window.removeEventListener('resize', checkDeviceAndPWA);
    } catch (error) {
      console.error('❌ Resize listener error:', error);
      return () => {};
    }
  }, []);

  // Additional mobile debugging
  useEffect(() => {
    console.log('🔍 StartScreen Component State:', {
      isMobile,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio
      },
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        availWidth: window.screen.availWidth,
        availHeight: window.screen.availHeight
      }
    });
  }, [isMobile]);

  const handlePlay = () => {
    onStart();
  };

  const handleHowToPlay = () => {
    onHowToPlay(); // Call the callback to handle navigation
  };

  const handleDownload = () => {
    // Show the installation steps modal
    setShowInstallModal(true);
  };

  // Debug mobile detection
  console.log('📱 StartScreen Mobile Detection:', { isMobile, userAgent: navigator.userAgent });

  return (
    <div 
      className="h-full flex items-center justify-center p-4 bg-teal-300"
      style={{
        // background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #2dd4bf 100%)',
        minHeight: '100dvh', // Dynamic viewport height for mobile
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Multiple fallback backgrounds for mobile */}
      <div 
        className="absolute inset-0 bg-teal-600"
        style={{ 
          zIndex: -1,
          backgroundColor: '#0d9488',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        }}
      />
      <div 
        className="absolute inset-0"
        style={{ 
          
          zIndex: -1,
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        }}
      />
      
      <div className="text-center text-black max-w-md w-full relative z-10">
        {/* App Icon/Logo */}
        <div className="mb-8">
          <div className="  rounded-3xl flex items-center justify-center">
            <img 
              src="/arcade/games/riddl/icons/riddl_icon.png" 
              alt="Riddl App Icon" 
              className="w-20 h-20 object-contain"
            />
          </div>
        </div>
        
        {/* App Name */}
        <h1 className="text-5xl font-bold mb-3 tracking-wide font-good-times">
          Riddl
        </h1>
        
        {/* Tagline */}
        <p className="text-xl opacity-90 font-medium mb-4">
          Daily Riddle Challenges
        </p>
        
        {/* Play status message */}
        {hasPlayedToday && (
          <p className="text-lg opacity-80 font-medium mb-8 text-gray-700">
            You've already completed today's riddle!
          </p>
        )}
        
        {/* Action Buttons */}
        <div className="flex flex-col gap-4 justify-center items-center">
          <button
            onClick={handlePlay}
            className={`w-40 py-2 px-6 rounded-full border-2 font-semibold text-lg transition-all duration-200 shadow-lg transform focus:outline-none ${
              hasPlayedToday 
                ? 'bg-gray-600 text-white border-gray-600 hover:bg-gray-700 focus:border-gray-700 active:border-gray-700' 
                : 'bg-black text-white border-black hover:border-transparent focus:border-transparent active:border-transparent'
            }`}
          >
            {hasPlayedToday ? 'View Game' : 'Play'}
          </button>
          
          <button
            onClick={handleHowToPlay}
            className="w-40 py-2 px-6 rounded-full border-2 border-black bg-transparent text-black font-semibold text-lg transition-all duration-200 shadow-lg transform hover:border-black focus:border-black active:border-black focus:outline-none"
          >
            How to Play
          </button>
          
          {/* Download App Button - Only show on mobile when not running as PWA */}
          {isMobile && !isPWA && (
            <button
              onClick={handleDownload}
              className="w-40 py-2 px-6 rounded-full border-2 border-black bg-transparent text-black font-semibold text-lg transition-all duration-200 shadow-lg transform hover:border-black focus:border-black active:border-black focus:outline-none"
            >
              Download
            </button>
          )}
        </div>
        
        {/* Footer */}
        <div className="mt-12 text-black/70 text-sm">
          <p>Challenge your mind daily</p>
        </div>
      </div>
      
      {/* Installation Steps Modal */}
      <InstallationStepsModal 
        isOpen={showInstallModal} 
        onClose={() => setShowInstallModal(false)} 
      />
    </div>
  );
};

export default StartScreen;
